import { useEffect, useState } from "react";
import type { ZatcaChainDto, ZatcaDocumentDto, ZatcaDocumentsDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { Card } from "../accounting/common";

// The e-invoice document of a posted sale or sales refund (phase 11b, rules Z-1 to Z-6, provisional): its counter,
// type, hashes and what it lacks, the XML to download, and, for a holder of the settings key, a check of the whole
// chain. Written by the server at posting. Not signed, not cleared or reported, not validated with ZATCA.

export function EInvoiceCard({ invoiceId, tobaccoFee }: { invoiceId: string; tobaccoFee?: string }) {
  const { t } = useLang();
  const s = useSession();
  const [doc, setDoc] = useState<ZatcaDocumentDto | null | undefined>(undefined);
  const [failed, setFailed] = useState<string | null>(null);
  const [chain, setChain] = useState<ZatcaChainDto | null>(null);
  useEffect(() => {
    api<ZatcaDocumentsDto>("GET", `/zatca/documents?invoiceId=${invoiceId}`).then((x) => setDoc(x.rows[0] ?? null), (e) => setFailed(errorText(t, e)));
  }, [invoiceId, t]);
  const check = () => api<ZatcaChainDto>("GET", "/zatca/chain").then(setChain, (e) => setFailed(errorText(t, e)));
  const body = failed ? <div className="dep-note"><Icon name="alert" /><span>{failed}</span></div>
    : doc === undefined ? <div className="muted">{t("loading")}</div>
    : doc === null ? <div className="empty sm" data-testid="einv-none"><Icon name="receipt" /><div>{t("einvNone")}</div></div>
    : <>
      <dl className="kv" style={{ ["--kv-cols" as string]: 1 }} data-testid="einv">
        <div><dt>{t("einvIcv")}</dt><dd className="num" data-testid="einv-icv">{doc.icv}</dd></div>
        <div><dt>{t("einvType")}</dt><dd data-testid="einv-type">{t(doc.typeCode === "381" ? "einvCreditNote" : "einvTaxInvoice")} · {t(doc.subtype === "0100000" ? "einvStandard" : "einvSimplified")}</dd></div>
        <div><dt>{t("einvHash")}</dt><dd className="num hash" data-testid="einv-hash">{doc.invoiceHash}</dd></div>
        <div><dt>{t("einvPrevHash")}</dt><dd className="num hash">{doc.previousHash}</dd></div>
      </dl>
      {doc.reversedInDelta && <p className="hint" data-testid="einv-reversed">{t("einvReversed")}</p>}
      {tobaccoFee && /[1-9]/.test(tobaccoFee) && <p className="hint" data-testid="einv-tobacco">{t("tobEinvNote", { x: tobaccoFee })}</p>}
      <ul className="rel-list" data-testid="einv-issues">{doc.issues.map((k) => <li key={k}><Icon name="alert" /><span>{t(`einvIssue_${k}`)}</span></li>)}</ul>
      <div className="einv-actions">
        <a className="btn" href={`/api/zatca/documents/${doc.id}/xml`} download data-testid="einv-xml"><Icon name="download" />{t("einvDownload")}</a>
        {s.can("setting") && <button className="btn" onClick={() => void check()} data-testid="einv-check"><Icon name="check" />{t("einvCheck")}</button>}
      </div>
      {chain && <p className={`hint ${chain.ok ? "" : "neg"}`} data-testid="einv-chain">{chain.ok ? t("einvChainOk", { n: String(chain.documents) }) : t("einvChainBad", { n: String(chain.problems.length) })}</p>}
    </>;
  return <Card title={t("einvTitle")}>
    <div className="dep-note" data-testid="einv-provisional"><Icon name="lock" /><span>{t("einvProvisional")}</span></div>
    {body}
  </Card>;
}
